import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
  Button,
  Drawer,
  FormatCurrencyForDisplay,
  PageStatus,
  SkeletonLoader,
} from '@unified-checkout/design-system';
import {
  ArrowRight,
  CircleExclamation,
  Download,
} from '@unified-checkout/design-system/icons/index';

import type { ITransferDrawer } from '@app/dashboard/types/innerLayout/balance_and_transfers';
import type {
  ITransferDetails,
  TTransferFileFormat,
} from '@app/dashboard/types/innerLayout/balance_and_transfers/single-transfer';
import type { TReportTemplateTypes } from '@app/dashboard/types/shared/modals/reports-template';
import { BALANCE_TRANSFERS_EVENTS_VALUES } from '@app/dashboard/constants/tracking_events/balance-and-transfers';
import { EMPTY_VALUE, FIVE_MINUTES_MS } from '@app/dashboard/constants/shared';
import { NOTIFICATION_STATUS } from '@app/dashboard/constants/notifier';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { INNER_ROUTES } from '@app/dashboard/constants/inner-routes';
import { useLangStore } from '@app/dashboard/store/lang';
import { useApplyThenNavigate } from '@app/dashboard/hooks/useApplyThenNavigate';
import { useNotificationStore } from '@app/dashboard/utils/notifier';
import { getSingleTransferDetails } from '@app/dashboard/services/inner_layout/balance_and_transfers/single-transfer';
import { REPORT_FILE_TYPES } from '@app/dashboard/constants/balance_and_transfers/single-transfer';
import ReportsTemplate from '@app/dashboard/components/shared/modals/ReportsTemplate';

import { formatAmountWithCurrency, isUAECountry } from '../../utils';
import { useTransferActionsTracking } from '../../hooks/useTransferActionsTracking';
import { useSingleTransferViewTracking } from '../../hooks/useSingleTransferViewTracking';
import TransferTimeline from '../TransferTimeline';
import TransferSummary from '../TransferSummary';
import TransferBadge from '../TransferBadge';

import styles from './transfer-drawer.module.scss';

const TransferDrawer = ({
  drawerState,
  handleCloseDrawer,
}: ITransferDrawer) => {
  const [isReportsTemplateOpen, setIsReportsTemplateOpen] = useState(false);
  const [selectedFileType, setSelectedFileType] =
    useState<TTransferFileFormat | null>(null);
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const applyThenNavigate = useApplyThenNavigate();
  const { show: isNotifyShown, state: notifyState } = useNotificationStore();
  const { lang } = useLangStore();

  const { open, transferId } = drawerState || {};

  const { data, isError, isFetching } = useQuery({
    queryKey: [QUERY_KEYS.GET_SINGLE_TRANSFER_DETAILS, transferId, lang],
    queryFn: ({ signal }) =>
      getSingleTransferDetails(transferId as string, signal),
    enabled: !!transferId,
    select: (response) => response?.data?.data as ITransferDetails,
    staleTime: FIVE_MINUTES_MS,
  });

  const {
    expected_arrival = null,
    transfer_summary,
    header,
    transfer_progress,
    actions = {
      ach_document_url: '',
      csv_url: '',
      pdf_url: '',
    },
    transfer_breakdown,
  } = data || {};

  const disableWhenNotifier =
    isNotifyShown && notifyState === NOTIFICATION_STATUS.SUCCESS;

  const {
    clickedReportTypeRef,
    handleDownloadAchDocument,
    handleDownloadTransactionsReport,
    downloadAch,
    exportTransferReport,
  } = useTransferActionsTracking({
    transferId: transferId ?? '',
    header,
    transactionCount: transfer_breakdown?.transaction_count ?? 0,
    entryPoint: BALANCE_TRANSFERS_EVENTS_VALUES.QUICK_VIEW,
  });

  const formattedAmount = header?.amount
    ? formatAmountWithCurrency({
        amount: header?.amount,
        currency: header?.currency,
        lang,
      })
    : EMPTY_VALUE;

  const handleViewFullDetails = () => {
    applyThenNavigate(
      handleCloseDrawer,
      INNER_ROUTES.SINGLE_TRANSFER.replace(':id', transferId || '')
    );
  };

  const handleOpenReportsModal = (type: TTransferFileFormat) => {
    handleCloseDrawer();
    setIsReportsTemplateOpen(true);
    setSelectedFileType(type);
  };

  const handleChooseReportTemplate = (templateType: TReportTemplateTypes) => {
    handleDownloadTransactionsReport(
      selectedFileType as TTransferFileFormat,
      templateType
    );
  };

  useEffect(() => {
    if (!transferId) {
      queryClient.cancelQueries({
        queryKey: [QUERY_KEYS.GET_SINGLE_TRANSFER_DETAILS, transferId, lang],
      });
    }
  }, [transferId]);

  useSingleTransferViewTracking({
    id: transferId as string,
    isError,
    isFetching,
    header,
    transfer_progress,
    transfer_summary,
    entryPoint: BALANCE_TRANSFERS_EVENTS_VALUES.QUICK_VIEW,
  });

  return (
    <div
      className={styles['transfer-details']}
      data-drawer-details-error={isError}
    >
      <Drawer
        open={open}
        closeOnClickOutside={
          !exportTransferReport.isPending || isReportsTemplateOpen
        }
        hasCloseIcon
        onCloseHandler={handleCloseDrawer}
        isOverlayAboveModal
        title={t('innerLayout.balanceTransfers.drawerDetails.title')}
      >
        {isError ? (
          <PageStatus
            status="error"
            title={t('innerLayout.errors.smthWentWrong')}
            description={t('innerLayout.errors.dataRetrievalErr')}
            icon={CircleExclamation}
          />
        ) : (
          <>
            {isFetching ? (
              <SkeletonLoader
                noOfRows={3}
                width="100%"
                height="9rem"
                type="bordered_box"
              />
            ) : (
              <section className={styles['transfer-details__overview']}>
                <div className={styles['transfer-details__overview-status']}>
                  <p
                    className={styles['transfer-details__overview-status-note']}
                  >
                    {t('innerLayout.balanceTransfers.singleTransfer.bank.note')}
                  </p>
                  {header && <TransferBadge header={header} />}
                </div>
                <p className={styles['transfer-details__overview-amount']}>
                  {FormatCurrencyForDisplay(formattedAmount)}
                </p>
                <span className={styles['transfer-details__overview-note']}>
                  {header?.subtitle}
                </span>
              </section>
            )}
            <TransferTimeline
              transferProgress={transfer_progress}
              header={header}
              isLoading={isFetching}
              transferId={transfer_summary?.transfer_id}
              settlementType={transfer_summary?.settlement_type_key}
              entryPoint={BALANCE_TRANSFERS_EVENTS_VALUES.QUICK_VIEW}
            />
            <TransferSummary
              isLoading={isFetching}
              transferSummary={transfer_summary}
              expectedArrival={expected_arrival}
              status={header?.status.key}
              transactionCount={transfer_breakdown?.transaction_count}
            />
            <section className={styles['transfer-details__actions']}>
              {isUAECountry() && (
                <div className={styles['transfer-details__actions-prof']}>
                  <Button
                    text={t(
                      'innerLayout.balanceTransfers.drawerDetails.actions.downloadProof'
                    )}
                    size="md"
                    leadingIcon={Download}
                    onClickHandler={() =>
                      handleDownloadAchDocument(actions.ach_document_url)
                    }
                    loading={downloadAch.isPending}
                    disabled={
                      downloadAch.isPending ||
                      isFetching ||
                      !actions.ach_document_url
                    }
                  />
                </div>
              )}
              <div className={styles['transfer-details__actions-row']}>
                <Button
                  text={t(
                    'innerLayout.balanceTransfers.drawerDetails.actions.downloadPdf'
                  )}
                  size="md"
                  leadingIcon={Download}
                  onClickHandler={() =>
                    handleOpenReportsModal(REPORT_FILE_TYPES.PDF)
                  }
                  variant="outlined"
                  disabled={
                    exportTransferReport.isPending ||
                    isFetching ||
                    disableWhenNotifier
                  }
                  loading={
                    exportTransferReport.isPending &&
                    clickedReportTypeRef.current === REPORT_FILE_TYPES.PDF
                  }
                />
                <Button
                  text={t(
                    'innerLayout.balanceTransfers.drawerDetails.actions.downloadCsv'
                  )}
                  size="md"
                  leadingIcon={Download}
                  onClickHandler={() =>
                    handleOpenReportsModal(REPORT_FILE_TYPES.CSV)
                  }
                  variant="outlined"
                  disabled={
                    exportTransferReport.isPending ||
                    isFetching ||
                    disableWhenNotifier
                  }
                  loading={
                    exportTransferReport.isPending &&
                    clickedReportTypeRef.current === REPORT_FILE_TYPES.CSV
                  }
                />
              </div>
            </section>
            <section className={styles['transfer-details__footer']}>
              <Button
                text={t(
                  'innerLayout.balanceTransfers.drawerDetails.actions.viewFullDetails'
                )}
                size="md"
                onClickHandler={handleViewFullDetails}
                variant="link"
                trailingIcon={ArrowRight}
              />
            </section>
          </>
        )}
      </Drawer>
      <ReportsTemplate
        isOpen={isReportsTemplateOpen}
        handleClose={() => setIsReportsTemplateOpen(false)}
        handleSubmit={handleChooseReportTemplate}
        isLoading={exportTransferReport.isPending}
      />
    </div>
  );
};

export default memo(TransferDrawer);
